import React, { useState } from "react";
import { Badge, Button, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { useRequest } from "@components/AppHook";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { ListingCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableRow = (props) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props.id,
    });

    const style = {
        opacity: isDragging ? 0.4 : undefined,
        transform: CSS.Translate.toString(transform),
        transition,
    };

    return (
        <tr ref={setNodeRef} style={style} className={props.className}>
            <td {...listeners} {...attributes}>
                {props.id === props.sortingId ? <Awesome type="spinner" spin /> : <Awesome type="reorder" />}
            </td>
            {props.children}
        </tr>
    );
};

export default function LandingCTItemsScreen() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { slug, block_id } = useParams();
    const apiUrl = url_reverse("api:landing_ct:block_items", { block_id });
    const { apiData, setApiData } = useAppApi(apiUrl);
    const { setDelData } = useAppContext();
    const { api_post } = useRequest();
    const [showAddItemModal, setShowAddItemModal] = useState(false);
    const [sortingId, setSortingId] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor));

    if (!apiData) return null;

    function handleDragEnd(event) {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = apiData.object_list.findIndex((item) => item.id === active.id);
            const newIndex = apiData.object_list.findIndex((item) => item.id === over.id);
            const new_list = arrayMove(apiData.object_list, oldIndex, newIndex);
            const ids = new_list.map((o) => o.id);

            const newApiData = { ...apiData };
            newApiData.object_list = new_list;
            setApiData(newApiData);

            setSortingId(active.id);

            api_post(null, apiUrl, {
                params: { action: "rearrange", ids },
                then: (resp) => {
                    if (resp.status === "ok") {
                        const sorted = { ...apiData };
                        sorted.object_list = resp.object_list;
                        setApiData(sorted);
                    }
                },
                finally: () => setSortingId(null),
            });
        }
    }

    const headerTools = (
        <>
            {apiData?.block_form && (
                <Link
                    to={url_reverse("admin:landing_ct:block_settings", { slug, block_id })}
                    className="btn btn-sm btn-dark"
                >
                    {t("Поля блока")}
                </Link>
            )}{" "}
            {apiData?.item_form && (
                <Button onClick={() => setShowAddItemModal(true)} className="btn btn-sm btn-dark">
                    {t("Добавить в блок")}
                </Button>
            )}
        </>
    );

    const header = (
        <>
            <Link to={url_reverse("admin:pockets:list")}>{t("Карманы")}</Link>
            <span> – </span>
            <Link to={url_reverse("admin:landing_ct:landings")}>{apiData.landing?.name || t("Лендинги")}</Link>
            <span> – </span>
            <Link to={url_reverse("admin:landing_ct:landing_items", { slug })}>
                {apiData.landing_name || t("Лендинг")}
            </Link>
            <span> – </span>
            {apiData.block_label || t("Блок")}
        </>
    );

    const addItemModal = () => {
        const backUrl = url_reverse("admin:landing_ct:block_items", { slug, block_id });
        return (
            <DevModal
                size="lg"
                show={showAddItemModal}
                onHide={() => setShowAddItemModal(false)}
                header={t("Добавить игру в блок")}
                body={
                    <RenderForm
                        prefix="item_form"
                        apiData={apiData}
                        setApiData={setApiData}
                        afterRespOK={() => {
                            setShowAddItemModal(false);
                            navigate(backUrl);
                        }}
                    />
                }
            />
        );
    };

    const tableRow = (item) => {
        const TableFieldPublished = ({ blockItem }) => {
            return (
                <>
                    {blockItem.gc_published && <Badge variant={"danger"}>GC</Badge>}
                    {blockItem.is_published_mobile && <Badge variant={"warning"}>MOB</Badge>}
                    {blockItem.vkplay_published && <Badge variant={"secondary"}>VKPLAY</Badge>}
                </>
            );
        };

        return (
            <SortableRow id={item.id} key={item.id} sortingId={sortingId} className={item.table_row_class}>
                <td>
                    {item.game ? (
                        <Link to={url_reverse("games:admin", { game_id: item.game.id })}>
                            {item.game?.base_locale_object?.name}
                        </Link>
                    ) : (
                        "-"
                    )}
                </td>
                <td>{item.date_start}</td>
                <td>{item.date_end}</td>
                <td>
                    <TableFieldPublished blockItem={item} />
                </td>
                <td>
                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => {
                                navigate(
                                    url_reverse("admin:landing_ct:block_item_edit", {
                                        slug,
                                        block_id,
                                        item_id: item.id,
                                    })
                                );
                            }}
                        >
                            <Awesome type="pencil" />
                        </Button>
                        <Button
                            variant="outline-danger"
                            className="ml-1"
                            size="sm"
                            onClick={() => {
                                const deleteUrl = url_reverse("api:landing_ct:block_items:crud", {
                                    block_id,
                                    item_id: item.id,
                                });
                                setDelData({
                                    url: deleteUrl,
                                });
                            }}
                        >
                            <Awesome type="trash-o" />
                        </Button>
                    </div>
                </td>
            </SortableRow>
        );
    };

    return (
        <DefaultLayout>
            <ListingCard header={header} headerTools={headerTools}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    {apiData?.item_form && (
                        <Table responsive hover className="dmg-table">
                            <thead>
                                <tr>
                                    <th style={{ width: "2%" }}></th>
                                    <th style={{ width: "35%" }}>{t("Игра")}</th>
                                    <th style={{ width: "15%" }}>{t("Дата начала")}</th>
                                    <th style={{ width: "15%" }}>{t("Дата окончания")}</th>
                                    <th style={{ width: "15%" }}>{t("Опубликовано")}</th>
                                    <th style={{ width: "18%" }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                <SortableContext items={apiData.object_list} strategy={verticalListSortingStrategy}>
                                    {apiData.object_list?.map(tableRow)}
                                </SortableContext>
                            </tbody>
                        </Table>
                    )}
                </DndContext>
                {addItemModal()}
            </ListingCard>
        </DefaultLayout>
    );
}
